import React, { useCallback, useRef, useState, useEffect } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Label } from './label';
import { usStates } from './state-input';
import { X } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { cn } from '@/lib/utils';

// ============================================================================
// Configuration
// ============================================================================

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// ============================================================================
// US States with DC included
// ============================================================================

const usStatesWithDC = [
  ...usStates,
  ...(usStates.some(s => s.value === 'DC')
    ? []
    : [{ label: 'District of Columbia', value: 'DC' }]),
];

// ============================================================================
// Address Parsing Utility
// ============================================================================

interface ParsedAddress {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  full_address: string;
}

const parseAddressComponents = (
  addressComponents: google.maps.GeocoderAddressComponent[]
): ParsedAddress => {
  const result: ParsedAddress = {
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    full_address: '',
  };

  if (!addressComponents || !Array.isArray(addressComponents)) {
    return result;
  }

  const componentsLong: Record<string, string> = {};
  const componentsShort: Record<string, string> = {};

  addressComponents.forEach(component => {
    if (!component?.types) return;

    const componentType = component.types[0] || '';
    const longName = component.long_name || '';
    const shortName = component.short_name || '';
    componentsLong[componentType] = longName;
    componentsShort[componentType] = shortName;
  });

  // Build address_line_1
  const streetNumber = componentsLong.street_number || '';
  const route = componentsLong.route || '';

  if (streetNumber && route) {
    result.address_line_1 = `${streetNumber} ${route}`;
  } else if (route) {
    result.address_line_1 = route;
  } else if (streetNumber) {
    result.address_line_1 = streetNumber;
  }

  result.address_line_2 = componentsLong.subpremise || '';

  // City detection with fallbacks
  result.city =
    componentsLong.locality ||
    componentsLong.postal_town ||
    componentsLong.sublocality_level_1 ||
    componentsLong.sublocality ||
    componentsLong.neighborhood ||
    componentsLong.administrative_area_level_2 ||
    componentsLong.administrative_area_level_3 ||
    '';

  // State mapping with DC support
  const stateShort = componentsShort.administrative_area_level_1 || '';
  const stateLong = componentsLong.administrative_area_level_1 || '';

  const stateByShort = usStatesWithDC.find(
    state => state.value.toUpperCase() === stateShort.toUpperCase()
  );
  const stateByLong = usStatesWithDC.find(
    state => state.label.toLowerCase() === stateLong.toLowerCase()
  );

  result.state = stateByShort?.value || stateByLong?.value || stateShort || '';
  result.postal_code = componentsLong.postal_code || componentsShort.postal_code || '';

  // Build full_address
  const addressParts: string[] = [];
  if (result.address_line_1) addressParts.push(result.address_line_1);
  if (result.address_line_2) addressParts.push(result.address_line_2);
  if (result.city) addressParts.push(result.city);
  if (result.state && result.postal_code) {
    addressParts.push(`${result.state} ${result.postal_code}`);
  } else if (result.state) {
    addressParts.push(result.state);
  } else if (result.postal_code) {
    addressParts.push(result.postal_code);
  }

  result.full_address = addressParts.join(', ');

  return result;
};

// ============================================================================
// Component Types
// ============================================================================

interface PlacesAutocompleteProps {
  onSelectPlace: (place: ParsedAddress) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  value?: string;
  label?: string;
  required?: boolean;
  onManualChange?: (val: string) => void;
  labelClassName?: string;
  inputClassName?: string;
  /** Optional class for the suggestions dropdown (e.g. dark theme: bg, border, and [&_li] styles) */
  suggestionsClassName?: string;
}

// ============================================================================
// Inner Component - Uses the Places library via useMapsLibrary hook
// ============================================================================

const PlacesAutocompleteInner: React.FC<PlacesAutocompleteProps> = ({
  onSelectPlace,
  placeholder = 'Enter US address...',
  disabled = false,
  className = '',
  value,
  label = 'Address Line 1',
  required = false,
  onManualChange,
  labelClassName,
  inputClassName,
  suggestionsClassName,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(value || '');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load the places library using useMapsLibrary hook
  const places = useMapsLibrary('places');

  // Services refs
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  // Store callback in ref to avoid stale closure
  const onSelectPlaceRef = useRef(onSelectPlace);
  onSelectPlaceRef.current = onSelectPlace;

  const isInternalChangeRef = useRef(false);

  // Sync external value with internal state (skip when change originated from typing)
  useEffect(() => {
    if (value !== undefined && !isInternalChangeRef.current && value !== inputValue) {
      setInputValue(value);
    }
    isInternalChangeRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Initialize Places services when the library is loaded
  useEffect(() => {
    if (places) {
      autocompleteServiceRef.current = new places.AutocompleteService();

      // PlacesService requires a div element (can be hidden)
      const div = document.createElement('div');
      placesServiceRef.current = new places.PlacesService(div);

      // Create a new session token
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
  }, [places]);

  // Handle input change - fetch predictions
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      isInternalChangeRef.current = true;
      setInputValue(value);
      onManualChange?.(value);

      if (!autocompleteServiceRef.current || !value.trim()) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);

      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'us' },
          types: ['address'],
          sessionToken: sessionTokenRef.current || undefined,
        },
        (preds, status) => {
          setIsLoading(false);

          if (status === google.maps.places.PlacesServiceStatus.OK && preds) {
            setPredictions(preds);
            setIsOpen(true);
          } else {
            setPredictions([]);
            setIsOpen(false);
          }
        }
      );
    },
    []
  );

  // Handle place selection
  const handlePlaceSelect = useCallback((placeId: string, description: string) => {
    if (!placesServiceRef.current || !places) {
      return;
    }

    // Update input value immediately
    setInputValue(description);
    setPredictions([]);
    setIsOpen(false);

    // Get place details
    placesServiceRef.current.getDetails(
      {
        placeId: placeId,
        fields: ['address_components', 'formatted_address'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (place, status) => {
        // Create a new session token after the request
        if (places) {
          sessionTokenRef.current = new places.AutocompleteSessionToken();
        }

        if (status === google.maps.places.PlacesServiceStatus.OK && place?.address_components) {
          const parsedAddress = parseAddressComponents(place.address_components);
          parsedAddress.full_address = place.formatted_address || parsedAddress.full_address;

          setInputValue(parsedAddress.full_address);

          if (onSelectPlaceRef.current) {
            onSelectPlaceRef.current(parsedAddress);
          }
        }
      }
    );
  }, [places]);

  // Clear input
  const handleClear = useCallback(() => {
    setInputValue('');
    setPredictions([]);
    setIsOpen(false);
    inputRef.current?.focus();

    if (onSelectPlaceRef.current) {
      onSelectPlaceRef.current({
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        postal_code: '',
        full_address: '',
      });
    }
  }, []);

  // Handle blur - close dropdown after a delay (to allow click on predictions)
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  }, []);

  // Handle focus - reopen if there are predictions
  const handleFocus = useCallback(() => {
    if (predictions.length > 0) {
      setIsOpen(true);
    }
  }, [predictions.length]);

  return (
    <div className={cn('relative w-full space-y-2', className)}>
      <Label className={labelClassName}>{label}{required && <span className='text-[#D4574E] ml-0.5'>*</span>}</Label>
      <div className='relative'>
        <Input
          ref={inputRef}
          type='text'
          placeholder={placeholder}
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoComplete='off'
          className={cn(isLoading ? 'pr-16' : 'pr-8', inputClassName)}
        />

        {isLoading && (
          <div className='absolute right-10 top-1/2 -translate-y-1/2'>
            <div className='h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent' />
          </div>
        )}

        {inputValue && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 p-0'
            onClick={handleClear}
            aria-label='Clear address'
          >
            <X className='h-4 w-4' />
          </Button>
        )}

        {/* Predictions dropdown */}
        {isOpen && predictions.length > 0 && (
          <ul
            className={cn(
              'absolute z-[450] mt-1 max-h-60 w-full overflow-auto rounded-md border shadow-lg',
              suggestionsClassName
                ? suggestionsClassName
                : 'border-input bg-popover [&_li]:hover:bg-accent [&_li]:hover:text-accent-foreground'
            )}
          >
            {predictions.map(prediction => (
              <li
                key={prediction.place_id}
                onClick={() => handlePlaceSelect(prediction.place_id, prediction.description)}
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm',
                  !suggestionsClassName && 'hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <span className='font-medium'>
                  {prediction.structured_formatting?.main_text || prediction.description}
                </span>
                {prediction.structured_formatting?.secondary_text && (
                  <span className={cn('ml-1', !suggestionsClassName && 'text-muted-foreground')}>
                    {prediction.structured_formatting.secondary_text}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component - Wraps with APIProvider
// ============================================================================

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = props => {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={cn('relative w-full space-y-2', props.className)}>
        <Label className={props.labelClassName}>{props.label || 'Address Line 1'}{props.required && <span className='text-[#D4574E] ml-0.5'>*</span>}</Label>
        <div className='flex h-10 w-full items-center rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive'>
          Google Maps API key not configured
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <PlacesAutocompleteInner {...props} />
    </APIProvider>
  );
};

export default PlacesAutocomplete;
